/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import * as React from 'react';
import { defineMessages } from 'react-intl';
import { LuisStepper } from 'src/core/components/luisStepper/LuisStepper';
import { LuisStepperStepType } from 'src/core/components/luisStepper/LuisStepperStep';
import { useLocalization } from 'src/core/hooks/useLocalization';
import { AuthoringRegion } from 'src/core/types/regionTypes';
import { getRegionScreen } from 'src/verticals/azureMigrationModal/hooks/useMultiRegionMigrationStateMachine';
import { getBackwardCompatibleRegion } from 'src/verticals/azureMigrationModal/MultiRegionMigrationModal';
import { UserMigrationInfoStore } from 'src/verticals/azureMigrationModal/stores/UserMigrationInfoStore';
import { MultiRegionMigrationSteps } from 'src/verticals/azureMigrationModal/types/azureMigrationModalTypes';

export type MigrationStepsNavigatorProps = {
    userMigrationInfo: UserMigrationInfoStore[];
    hideEmailStep: boolean;
    hideExportStep: boolean;
    activeStep: MultiRegionMigrationSteps;
};

const messages = defineMessages({
    start: {
        id: 'multiRegionMigrationModal.migrationModalStepsNavigator.start',
        defaultMessage: 'Introduction'
    },
    exportApps: {
        id: 'multiRegionMigrationModal.migrationModalStepsNavigator.exportApps',
        defaultMessage: 'Export apps shared with you'
    },
    emailCollaborators: {
        id: 'multiRegionMigrationModal.migrationModalStepsNavigator.emailCollaborators',
        defaultMessage: 'Notify collaborators'
    },
    regionalMigration: {
        id: 'multiRegionMigrationModal.migrationModalStepsNavigator.regionalMigration',
        defaultMessage: 'Regional migration'
    },
    accountMigration: {
        id: 'multiRegionMigrationModal.migrationModalStepsNavigator.accountMigration',
        defaultMessage: 'Account migration'
    },
    usMigration: {
        id: 'multiRegionMigrationModal.migrationModalStepsNavigator.usMigration',
        defaultMessage: 'West US migration'
    },
    euMigration: {
        id: 'multiRegionMigrationModal.migrationModalStepsNavigator.euMigration',
        defaultMessage: 'West Europe migration'
    },
    ausMigration: {
        id: 'multiRegionMigrationModal.migrationModalStepsNavigator.ausMigration',
        defaultMessage: 'East Australia migration'
    },
    finish: {
        id: 'multiRegionMigrationModal.migrationModalStepsNavigator.finish',
        defaultMessage: 'Review and finish'
    }
});

const getRegionTitle = (region: AuthoringRegion) => {
    switch (region) {
        case 'westus':
            return messages.usMigration;
        case 'westeurope':
            return messages.euMigration;
        case 'australiaeast':
            return messages.ausMigration;
        default:
            return messages.usMigration;
    }
};

const getStepStatus = (stepToEvaluate: MultiRegionMigrationSteps, activeStep: MultiRegionMigrationSteps) => {
    if (stepToEvaluate > activeStep) {
        return 'none';
    }
    if (stepToEvaluate === activeStep) {
        return 'active';
    }
    return 'completed';
};

const getRegionalMigrationStepStatus = (activeStep: MultiRegionMigrationSteps) => {
    if (activeStep === MultiRegionMigrationSteps.finish) {
        return 'completed';
    }
    if (activeStep > MultiRegionMigrationSteps.exportApps) {
        return 'active';
    }
    return 'none';
};

const getRegionStepStatus = (item: UserMigrationInfoStore, activeStep: MultiRegionMigrationSteps): LuisStepperStepType['state'] => {
    if (activeStep < MultiRegionMigrationSteps.regionalMigration) {
        return 'none';
    }
    if (getRegionScreen(item.region) === activeStep) {
        return 'active';
    }
    if (item.hasMigratedSuccessfully) {
        return 'completed';
    }
    return 'none';
};

/**
 * Displays the side navigator in the prepare container modal.
 */
export const MigrationModalStepsNavigator = (props: MigrationStepsNavigatorProps) => {
    const { userMigrationInfo, activeStep, hideEmailStep, hideExportStep } = props;
    const { localize } = useLocalization();

    const getSteps = (): LuisStepperStepType[] => {
        const steps: LuisStepperStepType[] = [
            {
                id: 'start',
                disabled: true,
                title: localize(messages.start),
                state: getStepStatus(MultiRegionMigrationSteps.start, activeStep)
            }
        ];

        if (!hideEmailStep) {
            steps.push({
                disabled: true,
                id: 'emailCollaborators',
                title: localize(messages.emailCollaborators),
                state: getStepStatus(MultiRegionMigrationSteps.emailCollaborators, activeStep)
            });
        }

        if (!hideExportStep) {
            steps.push({
                disabled: true,
                id: 'exportApps',
                title: localize(messages.exportApps),
                state: getStepStatus(MultiRegionMigrationSteps.exportApps, activeStep)
            });
        }

        steps.push({
            disabled: true,
            id: 'regionalMigration',
            children:
                getBackwardCompatibleRegion() === 'westus' || getBackwardCompatibleRegion() === 'centraluseuap'
                    ? userMigrationInfo.map(item => ({
                          disabled: true,
                          id: item.region,
                          title: localize(getRegionTitle(item.region)),
                          state: getRegionStepStatus(item, activeStep)
                      }))
                    : [
                          {
                              disabled: true,
                              id: 'chooseCsAccount',
                              title: localize(messages.regionalMigration),
                              state: getStepStatus(MultiRegionMigrationSteps.chooseCsAccount, activeStep)
                          }
                      ],
            state: getRegionalMigrationStepStatus(activeStep),
            title: localize(messages.regionalMigration)
        });

        steps.push({
            id: 'finish',
            disabled: true,
            title: localize(messages.finish),
            state: getStepStatus(MultiRegionMigrationSteps.finish, activeStep)
        });

        return steps;
    };

    return <LuisStepper steps={getSteps()} />;
};
